import { useState } from "react";
import { Card, CardContent, CardHeader, Divider, Grid, Paper, Stack, styled, Typography } from "~/common/mui";
import TargetGraph from "~/features/cms/homepagePersonalization/cpd/components/bonusAgreementTile/components/targetGraph/TargetGraph";
import { C_SURFACE_BRIGHT } from "~/common/components/icon/Colors";
import { formatPercent } from "~/common/utils/NumberFormatter";
import { DateFormatType, formatDate as formatDateUtil } from "~/common/utils/DateFormatter";
import { SelectChip } from "~/common/mui/Chip/SelectChip";
import { makeFormatCurrency } from "~/features/cms/homepagePersonalization/cpd/components/bonusAgreementTile/bonusAgeementUtil";
import { resolveApiUrl } from "~/utils/url/urlResolver";
import { getCsrfValue } from "~/common/utils/ApiHelper";
import { useTranslation } from "react-i18next";

type Period = "YEAR" | "QUARTER";

interface PeriodData {
  invoiceValue: number;
  orderbookValue: number;
  targets: {
    value: number;
    rate: number;
    gapToTarget?: number;
  }[];
  currentCashback?: number; // guaranteed cashback
  currentCashbackRate?: number; // cashback rate (between 0 and 1)
  gapToNextTarget?: number;
}

export interface BonusAgreementTileProps {
  data: {
    YEAR: PeriodData;
    QUARTER: PeriodData;
  };
  date: Date; // last update date
  currency: string;
  initialSelectedPeriod?: Period;
}

const BonusAgreementTile = ({ data, date, currency, initialSelectedPeriod = "YEAR" }: BonusAgreementTileProps) => {
  const { t } = useTranslation();
  const [selectedPeriod, setSelectedPeriod] = useState<Period>(initialSelectedPeriod);

  const currentData = data[selectedPeriod];

  const handlePeriodChange = (_event: React.MouseEvent<HTMLLIElement>, value: string) => {
    const period = value as Period;
    setSelectedPeriod(period);
    apiUpdateSelectedPeriod(period);
  };

  const formatCurrency = makeFormatCurrency(currency);

  // Sort targets to ensure they're in ascending order
  const sortedTargets = [...currentData.targets].sort((a, b) => a.value - b.value);
  const nextTarget = sortedTargets.find((target) => target.value > currentData.invoiceValue);

  const potentialCashback = nextTarget ? nextTarget.rate * nextTarget.value : undefined;

  const isYear = selectedPeriod === "YEAR";
  const hasReachedTarget = currentData.invoiceValue >= currentData.targets[0].value;

  let tiles: { label: string; data: string }[];
  if (isYear) {
    tiles = [
      {
        label: t("feature.cpd.bonusAgreement.cashback.guaranteed"),
        data: formatCurrency(currentData.currentCashback),
      },
      {
        label: t("feature.cpd.bonusAgreement.cashbackRate.current"),
        data: formatPercent(currentData.currentCashbackRate),
      },
      {
        label: t("feature.cpd.bonusAgreement.cashback.potentialNextTarget"),
        data: formatCurrency(potentialCashback),
      },
      {
        label: t("feature.cpd.bonusAgreement.gap.nextTarget"),
        data: formatCurrency(currentData.gapToNextTarget),
      },
    ];
  } else {
    if (hasReachedTarget) {
      tiles = [
        {
          label: t("feature.cpd.bonusAgreement.cashback.current"),
          data: formatCurrency(currentData.currentCashback),
        },
        {
          label: t("feature.cpd.bonusAgreement.cashbackRate.current"),
          data: formatPercent(currentData.currentCashbackRate),
        },
      ];
    } else {
      tiles = [
        {
          label: t("feature.cpd.bonusAgreement.cashback.potential"),
          data: formatCurrency(potentialCashback),
        },
        {
          label: t("feature.cpd.bonusAgreement.cashbackRate.potential"),
          data: formatPercent(currentData.targets[0]?.rate),
        },
      ];
    }
  }

  return (
    <Card sx={{ width: "100%" }}>
      <SpacedCardHeader
        title="Bonus Agreement"
        subheader={t("feature.cpd.lastUpdated", { date: formatDateUtil(date, DateFormatType.DATE) })}
        slotProps={{
          title: { variant: "titleLarge" },
          subheader: { variant: "bodySmall", color: "text.secondary" },
          action: { sx: { alignSelf: "center" } },
        }}
        action={
          <SelectChip
            value={selectedPeriod}
            values={[
              { label: t("feature.cpd.period.year"), value: "YEAR" },
              { label: t("feature.cpd.period.quarter"), value: "QUARTER" },
            ]}
            onChange={handlePeriodChange}
            showSelectedIcon
          />
        }
      />
      <Divider />
      <CardContent>
        <Stack gap={2}>
          <TargetGraph
            currency={currency}
            targets={currentData.targets}
            invoiceValue={currentData.invoiceValue}
            orderbookValue={currentData.orderbookValue}
            gapToNextTarget={currentData.gapToNextTarget}
          />
          <Grid container spacing={1}>
            {tiles.map((tile) => (
              <Grid size={6} key={tile.label} sx={{ display: "flex" }}>
                <DataCard {...tile} />
              </Grid>
            ))}
          </Grid>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default BonusAgreementTile;

const DataCard = ({ label, data }: { label: string; data: string }) => {
  return (
    <Paper variant="flat" component={Stack} gap={0.5} sx={{ p: 1, background: C_SURFACE_BRIGHT, width: "100%" }}>
      <Typography variant="bodySmall" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="titleLarge">{data}</Typography>
    </Paper>
  );
};

const SpacedCardHeader = styled(CardHeader)({
  height: "80px",
  "& .MuiCardHeader-content": {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
});

function apiUpdateSelectedPeriod(period: Period): Promise<boolean> {
  const apiUrl = resolveApiUrl(`/updateBonusViewPreference?preference=${period}`);

  return fetch(apiUrl, {
    cache: "no-cache",
    credentials: "same-origin",
    method: "PATCH",
    mode: "cors",
    headers: {
      CSRFToken: getCsrfValue(),
    },
    redirect: "follow",
  })
    .then((result) => {
      if (result.ok) {
        return true;
      } else {
        console.error(`Error updating selected period: ${result.status}`);
        return false;
      }
    })
    .catch((err) => {
      console.error(err);
      return false;
    });
}
